import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import loginHero from "../assets/home/login-logo.svg";
import Google from "../assets/google.png";
import useEncryption from "../useEncryption/useEncryption";
import axiosInstance from "../apiInstances/axiosInstance";
import { useGoogleLogin } from "@react-oauth/google";
import { MdOutlineMail } from "react-icons/md";
import { FaApple, FaRegEyeSlash } from "react-icons/fa";
import { IoEyeOutline } from "react-icons/io5";
import { RiLockPasswordLine } from "react-icons/ri";
import axios from "axios";
import { useCollections } from "../Hooks/useCollections/useCollections";
import homeLogo from "../assets/home/home.svg";

const LogInForm = () => {
  const navigate = useNavigate();
  const { decryptData } = useEncryption();
  const { refetch: refetchCollections } = useCollections();
  const [user, setUser] = useState(null);
  const [passHide, setPassHide] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  useEffect(() => {
    const checkAuth = localStorage.getItem("Token");
    if (checkAuth) {
      navigate("/");
    }
  }, [navigate]);

  const [fields, setFields] = useState({
    email: "",
    password: "",
  });

  const onChangeInput = (e) => {
    const value = e.target.value.trim();
    const name = e.target.name;

    setFields({
      ...fields,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    if (!fields.email || !fields.password) {
      toast.error("Please enter email and password");
      return;
    }

    const encryptedData = JSON.stringify({
      email: fields.email,
      password: fields.password,
      role: "user",
    });

    try {
      const res = await axiosInstance.post("signin", encryptedData);

      if (res?.data?.status) {
        localStorage.setItem("Token", res?.data?.data?.token);
        localStorage.setItem("email", res?.data?.data?.userData?.email);
        localStorage.setItem(
          "userData",
          JSON.stringify(res?.data?.data?.userData),
        );
        toast.success(res?.data?.message);
        refetchCollections();
        navigate("/dashboard");
      } else {
        if (res?.data?.page) {
          navigate(`${res?.data?.page}?email=${fields.email}`);
        } else {
          toast.error(res?.data?.message);
        }
      }
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  // Google login
  const GoogleLogin = useGoogleLogin({
    onSuccess: (codeResponse) => {
      setUser(codeResponse);
    },
    onError: (error) => console.log("Login Failed:", error),
  });

  async function loginWithGoogle(data) {
    const payload = {
      firstname: data?.family_name,
      lastname: data?.given_name,
      email: data?.email,
      role: "user",
    };

    try {
      const res = await axiosInstance.post("login-with-google", payload);

      localStorage.setItem("Token", res?.data?.data?.token);
      localStorage.setItem("email", payload.email);
      localStorage.setItem(
        "userData",
        JSON.stringify(res?.data?.data?.userData),
      );

      toast.success("Login success");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Google login failed");
    }
  }

  useEffect(() => {
    if (user?.access_token) {
      axios
        .get(
          `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${user.access_token}`,
          {
            headers: {
              Authorization: `Bearer ${user.access_token}`,
              Accept: "application/json",
            },
          },
        )
        .then((res) => {
          if (res.status === 200) {
            loginWithGoogle(res.data);
          }
        })
        .catch((err) => console.log(err));
    }
  }, [user]);

  const InputBox =
    "w-full text-sm rounded-full border border-[#E5E7EB] py-3.5 sm:py-4 pl-11 pr-4 outline-none focus:border-[#097FE6] focus:ring-none focus:ring-[#097FE6]";

  const pillOutline =
    "w-full flex items-center justify-center gap-3 rounded-full border border-[#E5E7EB] bg-white py-3.5 text-sm sm:text-base font-medium text-[#000929] hover:bg-[#F9FAFB] transition";

  const pillPrimary =
    "w-full flex items-center justify-center gap-2 rounded-full bg-[#097FE6] hover:bg-[#0866c4] text-white py-3.5 text-sm sm:text-base font-medium transition";

  return (
    <section className="min-h-screen flex flex-col lg:flex-row bg-white">
      <div className="order-1 flex flex-1 justify-center items-center flex-col lg:order-none lg:w-1/2 lg:min-h-screen px-4 xl:pl-24 pt-8 pb-10 lg:py-12">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="shrink-0 self-center xl:mt-0 mt-10 xl:self-start mb-6 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-[#097FE6] focus-visible:ring-offset-2 flex items-center gap-2"
          aria-label="Go to home"
        >
          <img
            src={homeLogo}
            alt="My Real Estate"
            className="h-8 w-auto max-w-[50px] object-left object-contain cursor-pointer -mt-2"
          />
          <p className="text-xl font-semibold text-[#000929]">
            myrealestate-ng.com
          </p>
        </button>
        <div className="flex flex-1 flex-col justify-center w-full max-w-md mx-auto lg:mx-0">
          <h1 className="xl:text-left text-center text-2xl sm:text-[40px] font-semibold text-[#000929] tracking-tight mb-8 sm:mb-10">
            Log into your account
          </h1>

          {showEmailForm && (
            <button
              type="button"
              className="text-sm text-[#097FE6] font-medium mb-6 -mt-2 self-start hover:underline"
              onClick={() => setShowEmailForm(false)}
            >
              ← Other ways to log in
            </button>
          )}

          {!showEmailForm ? (
            <div className="flex flex-col gap-3">
              <button
                type="button"
                className={pillPrimary}
                onClick={() => setShowEmailForm(true)}
              >
                <MdOutlineMail className="text-xl shrink-0" />
                Login with email
              </button>

              <div className="my-8 border-t border-[#E5E7EB]" />

              <button type="button" className={pillOutline}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden className="shrink-0">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                Login with X
              </button>

              <button type="button" className={pillOutline} onClick={GoogleLogin}>
                <img src={Google} alt="" className="w-5 h-5 shrink-0" />
                Login with Google
              </button>

              <button type="button" className={pillOutline}>
                <FaApple className="text-xl shrink-0" />
                Login with Apple
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="relative mb-3">
                <input
                  type="email"
                  className={InputBox}
                  placeholder="Email"
                  name="email"
                  value={fields.email}
                  onChange={onChangeInput}
                  autoComplete="email"
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
                <MdOutlineMail className="absolute top-1/2 -translate-y-1/2 left-4 text-[#9CA3AF] text-xl" />
              </div>

              <div className="relative mb-2">
                <input
                  type={passHide ? "text" : "password"}
                  className={InputBox}
                  placeholder="Password"
                  name="password"
                  value={fields.password}
                  onChange={onChangeInput}
                  autoComplete="current-password"
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
                <RiLockPasswordLine className="absolute top-1/2 -translate-y-1/2 left-4 text-[#9CA3AF] text-xl" />

                {passHide ? (
                  <IoEyeOutline
                    onClick={() => setPassHide(false)}
                    className="absolute top-1/2 -translate-y-1/2 right-4 text-[#9CA3AF] text-xl cursor-pointer"
                  />
                ) : (
                  <FaRegEyeSlash
                    onClick={() => setPassHide(true)}
                    className="absolute top-1/2 -translate-y-1/2 right-4 text-[#9CA3AF] text-xl cursor-pointer"
                  />
                )}
              </div>

              <div className="text-right mb-4">
                <button
                  type="button"
                  className="text-sm text-[#097FE6] font-semibold hover:underline"
                  onClick={() => navigate("/auth/forgot-password")}
                >
                  Forgot your password?
                </button>
              </div>

              <button type="button" className={pillPrimary} onClick={handleSubmit}>
                Sign in
              </button>
            </div>
          )}

          <p className="mt-8 text-sm text-[#6B7280] text-center ">
            Don&apos;t have an account?{" "}
            <button
              type="button"
              className="font-semibold text-[#000929] hover:text-[#097FE6]"
              onClick={() => navigate("/sign-up")}
            >
              Sign up
            </button>
          </p>

          <button
            type="button"
            className="mt-4 w-full text-sm font-semibold text-[#097FE6] underline underline-offset-2 decoration-[#097FE6] hover:text-[#0866c4] hover:decoration-[#0866c4] text-center"
            onClick={() => navigate("/")}
          >
            Back to home
          </button>
        </div>

        <p className="mt-auto pt-6 xl:pt-10 text-xs sm:text-sm text-[#9CA3AF] max-w-md mx-auto lg:mx-0 lg:max-w-none  xl:text-left text-center ">
          By continuing, you agree to our{" "}
          <span className="underline text-[#000929] cursor-pointer">Terms of Service</span>
          {" "}and{" "}
          <span className="underline text-[#000929] cursor-pointer">Privacy Policy</span>
          .
        </p>
      </div>

      <div
        className="order-2  w-full min-h-[240px] sm:min-h-[320px] lg:order-none lg:w-1/2 lg:min-h-screen items-center justify-center px-8 py-10 lg:p-12 bg-gradient-to-l from-[#097FE6] to-[#FFFFFF] xl:flex hidden"
        aria-hidden
      >
        <img
          src={loginHero}
          alt=""
          className="w-full max-w-[min(100%,420px)] lg:max-w-[min(100%,614px)] h-auto object-contain select-none pointer-events-none"
        />
      </div>
    </section>
  );
};

export default LogInForm;
